import {PayloadAction} from '@reduxjs/toolkit';
import {AxiosResponse} from 'axios';
import {DateTime} from 'luxon';
import {all, call, put, select, takeLatest} from 'redux-saga/effects';

import {Markets} from 'models/Market';
import {isProduction} from 'services/api/api';
import {getMarkets, getMarketsFallback} from 'services/api/markets';

import {MarketsActions} from './markets.reducer';
import {
  marketsLastQueryTimeSelector,
  marketsSelector,
} from './markets.selectors';

/**
 * This sets the cache time to live for the markets slice.
 */
export const CACHE_TTL_MINS = 30;

export function* handleGetMarkets(_action: PayloadAction) {
  const lastQueryTime: string = yield select(marketsLastQueryTimeSelector);
  const cachedMarkets: Markets = yield select(marketsSelector);
  const durationSinceLastQuery = DateTime.fromISO(lastQueryTime)
    ?.diffNow('minutes')
    .negate(); // Negate to get duration since now/local

  if (
    lastQueryTime &&
    cachedMarkets.length > 0 &&
    durationSinceLastQuery?.minutes < CACHE_TTL_MINS
  ) {
    yield put(MarketsActions.getMarketsCachedSuccess());
    return;
  }

  try {
    const response: AxiosResponse<Markets> = yield call(getMarkets);
    const {data} = response;
    yield put(MarketsActions.getMarketsSuccess(data));
  } catch (error) {
    try {
      if (!isProduction) {
        throw 'No fallback in dev / staging';
      }
      const response: AxiosResponse<Markets> = yield call(getMarketsFallback);
      const {data} = response;
      yield put(MarketsActions.getMarketsSuccess(data));
    } catch (errFallback) {
      yield put(MarketsActions.getMarketsFailure());
    }
  }
}

export default function* marketsSagas() {
  yield all([takeLatest(MarketsActions.getMarkets, handleGetMarkets)]);
}
